import React from 'react';
import { CheckCircle, Clock, XCircle, User, Building2 } from 'lucide-react';
import { PilgrimGuideLink } from '../../types/pilgrimGuide';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  link: PilgrimGuideLink;
};

export const LinkingTableRow = ({ link }: Props) => {
  const { translations } = useLanguage(); // Access translations

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'approved':
        return {
          icon: CheckCircle,
          color: 'text-green-500',
          bg: 'bg-green-50',
          label: translations?.linkingTableRow?.status?.approved || 'Approved',
        };
      case 'rejected':
        return {
          icon: XCircle,
          color: 'text-red-500',
          bg: 'bg-red-50',
          label: translations?.linkingTableRow?.status?.rejected || 'Rejected',
        };
      default:
        return {
          icon: Clock,
          color: 'text-yellow-500',
          bg: 'bg-yellow-50',
          label: translations?.linkingTableRow?.status?.pending || 'Pending',
        };
    }
  };

  const status = getStatusConfig(link.status);
  const StatusIcon = status.icon;

  return (
    <tr className="hover:bg-gray-50">
      {/* Pilgrim Info */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div
            className={`p-2 rounded-full ${
              link.gender === 'male' ? 'bg-blue-50' : 'bg-pink-50'
            }`}
          >
            <User
              className={
                link.gender === 'male' ? 'text-blue-500' : 'text-pink-500'
              }
              size={20}
            />
          </div>
          <div>
            <div className="font-medium">{link.pilgrimName}</div>
            <div className="text-sm text-gray-500">
              {translations?.linkingTableRow?.labels?.passport || 'Passport'}:{' '}
              {link.passportNumber}
            </div>
            <div className="text-sm text-gray-500">{link.residenceCountry}</div>
          </div>
        </div>
      </td>

      {/* Guide Info */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-primary-50">
            <User className="text-primary-500" size={20} />
          </div>
          <div className="font-medium">{link.guideName}</div>
        </div>
      </td>

      {/* Organizer Info */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-secondary-50">
            <Building2 className="text-secondary-500" size={20} />
          </div>
          <div className="font-medium">{link.organizerName}</div>
        </div>
      </td>

      {/* Status */}
      <td className="px-6 py-4">
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm ${status.bg} ${status.color}`}
        >
          <StatusIcon size={16} />
          {status.label}
        </span>
      </td>
    </tr>
  );
};
